import pdfmake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import html2canvas from 'html2canvas';
import { cloneDeep } from 'lodash';
import pdfImage from '../json/pdfImages.json';
import i18n from './i18n.config';

export default class PDFMaker {
	constructor() {
		pdfmake.vfs = pdfFonts.pdfMake.vfs;
		pdfmake.fonts = {
			Roboto: {
				normal: 'Roboto-Regular.ttf',
				bold: 'Roboto-Medium.ttf',
				italics: 'Roboto-Italic.ttf',
				bolditalics: 'Roboto-MediumItalic.ttf',
			},
		};

		this.pdfData = {
			footer(currentPage, pageCount) {
				return {
					columns: [
						{ width: '*', text: '' },
						{
							stack: [
								{
									canvas: [
										{
											type: 'rect',
											x: 0,
											y: 0,
											w: 20,
											h: 20,
											r: 4,
											color: '#0172C1',
										},
									],
								},
								{
									text: currentPage.toString(),
									fontSize: 10,
									bold: true,
									color: '#FFFFFF',
									relativePosition: { x: 0, y: -16 },
									alignment: 'center',
									width: 20,
								},
							],
							width: 20,
						},
					],
					margin: [0, 20, 20, 0],
				};
			},
			header(currentPage, pageCount, pageSize) {
				return [
					{
						margin: [20, 10, 20, 0],
						columns: [
							{
								image: pdfImage.timac_image,
								width: 200,
							},
							{
								margin: [100, 0, 0, 0],
								image: pdfImage.contact_image,
								width: 160,
							},
							{
								margin: [110, 10, 0, 0],
								image: pdfImage.roullier_image,
								width: 30,
							},
							{
								margin: [125, 10, 0, 0],
								image: pdfImage.iso_image,
								width: 30,
							},
						],
					},
				];
			},
			content: [],
			pageSize: 'A4',
			pageMargins: [20, 80, 20, 60],
			pageBreakBefore: function (currentNode) {
				if (currentNode.headlineLevel === 1 && currentNode.startPosition) {
					// A4 height = 841.89pt, bottom margin = 60pt
					const pageBottom = 841.89 - 60;
					const remainingSpace = pageBottom - currentNode.startPosition.top;
					// Need room for: title (~24pt) + table header (~14pt) + 1 data row (~14pt) + padding (~28pt)
					if (remainingSpace < 80) {
						return true;
					}
				}
				return false;
			},
			styles: {
				blueContainerTitle: {
					margin: [4, 4, 4, 4],
					fontSize: 12,
					color: '#0573B2',
					bold: true,
				},
				h1: { fontSize: 18, bold: true, margin: [0, 14, 0, 8] },
				h2: {
					fontSize: 16,
					bold: true,
					margin: [0, 20, 0, 8],
					color: '#444',
				},
				h3: {
					fontSize: 14,
					bold: true,
					margin: [0, 22, 0, 8],
					color: '#666',
				},
				tableHeaderCell: {
					margin: [1, 1, 1, 1],
					fontSize: 8,
					fillColor: '#EBEBEB',
					color: '#545454',
					bold: false,
				},
				tableNormalCell: {
					margin: [2, 2, 2, 2],
					fontSize: 8,
					fillColor: '#fff',
					color: '#000000',
					bold: false,
				},
				tableNormalCellStriped: {
					margin: [2, 2, 2, 2],
					fontSize: 8,
					fillColor: '#fff',
					color: '#000000',
					bold: false,
				},
				blueContainer: {
					margin: [0, 12, 0, 0],
				},
				reportTitleStyle: {
					margin: [0, 8, 0, 0],
					fontSize: 16,
					color: '#0272BA',
					bold: true,
				},
				reportSubtitleStyle: {
					margin: [0, 4, 0, 0],
					fontSize: 12,
					color: '#555555',
					bold: false,
				},
				noBordersCell: {
					margin: [8, 2, 8, 2],
					fontSize: 8,
					bold: false,
				},
				noBordersCellSignature: {
					margin: [8, 2, 8, 2],
					fontSize: 8,
					bold: false,
				},
				bigger: {
					fontSize: 15,
					italics: true,
				},
				commentStyleTitle: {
					fontSize: 8,
					italics: false,
					bold: false,
				},
				commentStyle: {
					fontSize: 8,
					italics: false,
				},
				normalStyle: {
					fontSize: 8,
				},
				normalStyle2: {
					fontSize: 12,
				},
				observationsTitle: {
					fontSize: 8,
					margin: [0, 8, 0, 4],
				},
				observationsList: {
					fontSize: 8,
					margin: [10, 0, 0, 0],
				},
				legalText: {
					margin: [8, 24, 8, 8],
					fontSize: 8,
					bold: false,
					color: '#000000',
				},
				footer: {
					margin: [10, 10, 10, 10],
				},
			},
		};

		// Layout sin bordes para tablas específicas
		this.noBordersLayout = {
			hLineWidth: function (i, node) {
				return 0;
			},
			vLineWidth: function (i, node) {
				return 0;
			},
			paddingLeft: function (i, node) {
				return 2;
			},
			paddingRight: function (i, node) {
				return 2;
			},
			paddingTop: function (i, node) {
				return 2;
			},
			paddingBottom: function (i, node) {
				return 2;
			},
		};

		// Helper function to check if a row contains grouped row labels
		const isGroupedRowLabel = (row) => {
			for (const cell of row) {
				if (cell && typeof cell === 'object' && cell.text) {
					const text = typeof cell.text === 'string' ? cell.text : '';
					if (
						text === 'Sectores' ||
						text.startsWith('Riego (h)') ||
						text.startsWith('Horas de riego') ||
						text.startsWith('T. Riego')
					) {
						return true;
					}
				} else if (typeof cell === 'string') {
					if (
						cell === 'Sectores' ||
						cell.startsWith('Riego (h)') ||
						cell.startsWith('Horas de riego') ||
						cell.startsWith('T. Riego')
					) {
						return true;
					}
				}
			}
			return false;
		};

		// Layout personalizado con borde inferior (no aplica a cabeceras)
		this.customLayout = {
			hLineWidth: function (i, node) {
				// No mostrar línea en top (i=0)
				if (i === 0) {
					return 0;
				}

				// No mostrar separador encima de filas agrupadas (sectores, T. riego)
				const currentRow = node.table.body[i];
				if (currentRow && isGroupedRowLabel(currentRow)) {
					return 0;
				}

				// Comprobar si la fila anterior es una fila de cabecera
				const previousRow = node.table.body[i - 1];
				if (previousRow) {
					const headerCells = previousRow.filter(
						(cell) =>
							cell &&
							typeof cell === 'object' &&
							cell.style === 'tableHeaderCell',
					);
					const normalCells = previousRow.filter(
						(cell) =>
							cell &&
							typeof cell === 'object' &&
							(cell.style === 'tableNormalCell' ||
								cell.style === 'tableNormalCellStriped'),
					);

					// Si hay cabeceras y NO hay celdas normales, la línea debe ser blanca y de 2px
					if (headerCells.length > 0 && normalCells.length === 0) {
						return 2;
					}
				}

				return 1;
			},
			vLineWidth: function (i, node) {
				// Verificar si hay filas de cabecera en la tabla
				let hasHeaderRows = false;
				if (node.table.body && node.table.body.length > 0) {
					for (
						let rowIndex = 0;
						rowIndex < node.table.body.length;
						rowIndex++
					) {
						const row = node.table.body[rowIndex];
						const headerCells = row.filter(
							(cell) =>
								cell &&
								typeof cell === 'object' &&
								cell.style === 'tableHeaderCell',
						);
						const normalCells = row.filter(
							(cell) =>
								cell &&
								typeof cell === 'object' &&
								(cell.style === 'tableNormalCell' ||
									cell.style === 'tableNormalCellStriped'),
						);

						// Si esta fila tiene cabeceras y no tiene celdas normales
						if (headerCells.length > 0 && normalCells.length === 0) {
							hasHeaderRows = true;
							break;
						} else if (normalCells.length > 0) {
							// Si encontramos celdas normales, ya no hay más cabeceras
							break;
						}
					}
				}

				// Líneas verticales de 2px si hay cabeceras, 1px si no
				return hasHeaderRows ? 2 : 1;
			},
			hLineColor: function (i, node) {
				if (i === 0) return '#D2D2D2';

				// Comprobar si la fila anterior es una fila de cabecera
				const previousRow = node.table.body[i - 1];
				if (previousRow) {
					// Buscar celdas con estilo de cabecera y celdas normales
					const headerCells = previousRow.filter(
						(cell) =>
							cell &&
							typeof cell === 'object' &&
							cell.style === 'tableHeaderCell',
					);
					const normalCells = previousRow.filter(
						(cell) =>
							cell &&
							typeof cell === 'object' &&
							(cell.style === 'tableNormalCell' ||
								cell.style === 'tableNormalCellStriped'),
					);

					// Si hay cabeceras y NO hay celdas normales, la línea debe ser blanca
					if (headerCells.length > 0 && normalCells.length === 0) {
						return '#FFFFFF';
					}
				}

				// Líneas grises para el resto
				return '#D2D2D2';
			},
			vLineColor: function (i, node) {
				// Todas las líneas verticales en blanco
				return '#FFFFFF';
			},
			paddingLeft: function (i, node) {
				return 2;
			},
			paddingRight: function (i, node) {
				return 2;
			},
			paddingTop: function (i, node) {
				// Reducir padding para filas agrupadas (sectores, T. riego)
				const currentRow = node.table.body[i];
				if (currentRow && isGroupedRowLabel(currentRow)) {
					return 0;
				}
				// Reducir padding superior de la fila siguiente a la última fila agrupada
				if (i > 0) {
					const previousRow = node.table.body[i - 1];
					if (previousRow && isGroupedRowLabel(previousRow)) {
						return 0;
					}
				}
				return 2;
			},
			paddingBottom: function (i, node) {
				// Reducir padding para filas que preceden a filas agrupadas (dosis, sectores)
				const nextRow = node.table.body[i + 1];
				if (nextRow && isGroupedRowLabel(nextRow)) {
					return 0;
				}
				// Reducir padding inferior de cualquier fila agrupada (última del periodo)
				const currentRow = node.table.body[i];
				if (currentRow && isGroupedRowLabel(currentRow)) {
					return 0;
				}
				return 2;
			},
		};
	}

	formatNumberEuropean(value) {
		// Formatear números con punto para miles y coma para decimales
		if (value == null || value === '' || value === 0) return value;

		// Si es un número o un string que representa un número
		const num = typeof value === 'number' ? value : parseFloat(value);

		if (isNaN(num)) return value;

		// Formatear con punto para miles y coma para decimales
		return num.toLocaleString('es-ES', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 2,
		});
	}

	getBlueContainer(title, breakTabla = true) {
		let tableOptions = {};

		if (breakTabla) {
			// Cuando breakTabla es true, devolver estructura con stack
			return {
				style: 'blueContainer',
				headlineLevel: 1,
				stack: [
					{
						table: {
							widths: ['*'],
							body: [[{ text: title, style: 'blueContainerTitle' }]],
						},
						layout: {
							hLineWidth: function (i, node) {
								return i === 1 ? 1 : 0;
							},
							vLineWidth: function (i, node) {
								return 0;
							},
							hLineColor: function (i, node) {
								return i === 1 ? '#0573B2' : '#000';
							},
							paddingLeft: function (i, node) {
								return 0;
							},
							paddingRight: function (i, node) {
								return 0;
							},
							paddingTop: function (i, node) {
								return 0;
							},
							paddingBottom: function (i, node) {
								return 0;
							},
						},
					},
					{
						table: {
							widths: ['*'],
							body: [[]],
						},
						layout: 'noBorders',
					},
				],
			};
		}

		// Estructura con stack (igual que breakTabla=true) para evitar repetición de título
		return {
			style: 'blueContainer',
			headlineLevel: 1,
			stack: [
				{
					table: {
						widths: ['*'],
						body: [[{ text: title, style: 'blueContainerTitle' }]],
					},
					layout: {
						hLineWidth: function (i, node) {
							return i === 1 ? 1 : 0;
						},
						vLineWidth: function (i, node) {
							return 0;
						},
						hLineColor: function (i, node) {
							return i === 1 ? '#0573B2' : '#000';
						},
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 0;
						},
						paddingBottom: function (i, node) {
							return 0;
						},
					},
				},
				{
					table: {
						widths: ['*'],
						body: [[]],
					},
					layout: {
						hLineWidth: function (i, node) {
							return 0;
						},
						vLineWidth: function (i, node) {
							return 0;
						},
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 2;
						},
						paddingBottom: function (i, node) {
							return 8;
						},
					},
				},
			],
		};
	}

	insertTableWithoutBorders(data) {
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			this.pdfData.content.push({
				table: {
					widths: [],
					body: [[]],
				},
			});
			return;
		}
		const blueContainer = this.getBlueContainer(
			'Datos de la explotación',
			false,
		);

		const table = {
			widths: Array(data[0].length).fill(
				`${(100 / data[0].length).toFixed(2)}%`,
			),
			body: [],
		};

		data.forEach((row) => {
			const headers = row.map((cell) => {
				if (cell.title.length === 0 && cell.text.length === 0) {
					return {
						style: 'noBordersCell',
						text: '',
					};
				}

				return {
					style: 'noBordersCell',
					text: [
						{
							text: `${cell.title}: `,
							fontSize: 8,
							bold: false,
							color: '#545454',
						},
						{ text: cell.text, fontSize: 8, bold: false, color: '#000000' },
					],
				};
			});
			table.body.push(headers);
		});

		blueContainer.stack[1].table.body[0].push({ table, layout: this.noBordersLayout });

		this.pdfData.content.push(blueContainer);
	}

	insertInfoTable(data) {
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			return;
		}

		const table = {
			widths: Array(data[0].length).fill(
				`${(100 / data[0].length).toFixed(2)}%`,
			),
			body: [],
		};

		data.forEach((row) => {
			const cells = row.map((cell) => {
				const title = cell?.title ?? '';
				const text = cell?.text ?? '';
				if (title.length === 0 && String(text).length === 0) {
					return { style: 'noBordersCell', text: '' };
				}
				return {
					style: 'noBordersCell',
					text: [
						{ text: `${title}: `, fontSize: 8, bold: false, color: '#545454' },
						{ text: String(text), fontSize: 8, bold: false, color: '#000000' },
					],
				};
			});
			table.body.push(cells);
		});

		this.pdfData.content.push({
			table,
			layout: this.noBordersLayout,
			margin: [0, 4, 0, 0],
		});
	}

	insertPaoNeedsAndDose(opts) {
		const {
			title,
			needsLabel,
			needsHeader,
			needsValues,
			doseSubtitle,
			doseHeader,
			productRows,
		} = opts;

		const fmt = (v) =>
			typeof v === 'number' ? this.formatNumberEuropean(v) : v ?? '';
		const H = (text, extra = {}) => ({ style: 'tableHeaderCell', text, ...extra });
		const W = (extra = {}) => ({
			text: '',
			fillColor: '#FFFFFF',
			border: [false, false, false, false],
			...extra,
		});
		const C = (text, extra = {}) => ({
			style: 'tableNormalCell',
			text: fmt(text),
			...extra,
		});

		const blueContainer = this.getBlueContainer(title, false);
		const widths = ['20%', '20%', '10%', '10%', '10%', '10%', '10%', '10%'];

		const needsBody = [
			[W(), W(), ...needsHeader.map((t) => H(t, { alignment: 'center' }))],
			[
				W(),
				H(needsLabel, { alignment: 'right', noWrap: true }),
				...needsValues.map((v) => C(v, { alignment: 'center' })),
			],
		];

		const doseBody = [
			[W(), H(doseSubtitle, { colSpan: 7, alignment: 'center' }), {}, {}, {}, {}, {}, {}],
			doseHeader.map((t, i) => H(t, { alignment: i === 0 ? 'left' : 'center' })),
			...productRows.map((row) =>
				row.map((cell, i) => C(cell, { alignment: i === 0 ? 'left' : 'center' })),
			),
		];

		const cl = this.customLayout;
		const layoutNoBottom = {
			...cl,
			hLineWidth: (i, node) =>
				i === node.table.body.length ? 0 : cl.hLineWidth(i, node),
		};

		blueContainer.stack[1].table.body[0].push({
			stack: [
				{
					table: { widths, body: needsBody, dontBreakRows: true },
					layout: layoutNoBottom,
					margin: [0, 2, 0, 0],
				},
				{
					table: { widths, body: doseBody, dontBreakRows: true },
					layout: cl,
					margin: [0, 3, 0, 0],
				},
			],
		});
		this.pdfData.content.push(blueContainer);
	}

	insertTableGeneric(
		title,
		data,
		lastHeaderCell = false,
		extraHeader = '',
		striped = false,
		widthsColumns = null,
		lastRowBold = false,
		hideZeroNumeric = false,
		showTitle = true,
		preventRowSplit = false,
	) {
		let widthsColumnsPrepared = null;
		if (widthsColumns && Array.isArray(widthsColumns)) {
			const numericSum = widthsColumns
				.filter((width) => typeof width === 'number')
				.reduce((acc, curr) => acc + curr, 0);

			const autoCount = widthsColumns.filter(
				(width) => width === 'auto',
			).length;

			const remaining = 100 - numericSum;
			const autoWidth = autoCount > 0 ? remaining / autoCount : 0;

			widthsColumnsPrepared = widthsColumns.map((width) => {
				if (typeof width === 'number') {
					return `${width}%`;
				}
				if (width === 'auto') {
					return `${autoWidth}%`;
				}
				return width;
			});
		}
		// --- Modo tabla paginada ---
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			this.pdfData.content.push({
				table: {
					widths: [],
					body: [[{ text: title }]],
				},
			});
			return;
		}
		const isPaginated =
			Array.isArray(data) && data.length > 0 && data[0].headers && data[0].rows;

		if (isPaginated) {
			data.forEach((page, pageIdx) => {
				const shouldShowTitle = showTitle && (pageIdx === 0);
				const blueContainer = shouldShowTitle
					? this.getBlueContainer(title, false)
					: null;

				const table = {
					widths:
						widthsColumnsPrepared ||
						Array(page.headers.length).fill(
							`${(100 / page.headers.length).toFixed(2)}%`,
						),
					headerRows: 1,
					keepWithHeaderRows: 1,
					dontBreakRows: true,
					body: [],
				};

				const alignment = Array(page.headers.length).fill('left');

				// Cabecera
				table.body.push(
					page.headers.map((cellText, cellIndex) => ({
						style: 'tableHeaderCell',
						text: cellText,
						alignment: alignment[cellIndex],
					})),
				);

				// Filas
				page.rows.forEach((row, index) => {
					// Zebra style table
					const style = striped
						? index % 2 === 0
							? 'tableNormalCellStriped'
							: 'tableNormalCell'
						: 'tableNormalCell';

					table.body.push(
						row.map((cellText, cellIndex) => {
							// ROWSPAN GENERALIZADO
							if (
								cellText &&
								typeof cellText === 'object' &&
								cellText.hasOwnProperty('value') &&
								cellText.hasOwnProperty('rowspan')
							) {
								if (cellText.value && cellText.rowspan >= 1) {
									const cell = {
										style,
										text: cellText.value,
										alignment: alignment[cellIndex],
									};
									if (cellText.rowspan > 1) {
										cell.rowSpan = cellText.rowspan;
									}
									return cell;
								} else {
									return {
										style,
										text: '',
										alignment: alignment[cellIndex],
										border: [false, false, false, false],
									};
								}
							}
							// FIN ROWSPAN
							// Formatear números con formato europeo
							const formattedText =
								typeof cellText === 'number'
									? this.formatNumberEuropean(cellText)
									: cellText;
							return {
								style,
								text: formattedText,
								alignment: alignment[cellIndex],
							};
						}),
					);
				});

				if (blueContainer) {
					blueContainer.stack[1].table.body[0].push({
						table,
						layout: this.customLayout,
						margin: [0, 2, 0, 0],
					});
					this.pdfData.content.push(blueContainer);
				} else {
					this.pdfData.content.push({
						table,
						layout: this.customLayout,
						margin: [0, 2, 0, 0],
					});
				}

				// Salto de página entre tablas, excepto la última
				if (pageIdx < data.length - 1) {
					this.pdfData.content.push({ text: '', pageBreak: 'after' });
				}
			});
			return;
		}

		// --- Modo tabla única (sin paginación) ---
		const blueContainer = showTitle ? this.getBlueContainer(title, false) : null;

		const table = {
			widths:
				widthsColumnsPrepared ||
				Array(data[0].length).fill(`${(100 / data[0].length).toFixed(2)}%`),
			headerRows: extraHeader === '' ? 1 : 2,
			keepWithHeaderRows: 1,
			dontBreakRows: true,
			body: [],
		};

		const alignment = Array(data[0].length).fill('left');

		if (data.length > 1) {
			const indexStart = extraHeader === '' ? 1 : 2;
			for (let i = indexStart; i < data.length; i += 1) {
				data[i].forEach((cell, index) => {
					if (typeof cell === 'number') {
						alignment[index] = 'right';
					}
				});
			}
		}

		if (extraHeader !== '') {
			const len = data[0].length;
			table.body.push([
				{
					text: data[0][0] || '',
					rowSpan: 2,
					alignment: alignment[0],
					margin: [1, 7, 1, 1],
					style: 'tableHeaderCell',
				},
				{
					text: extraHeader,
					colSpan: len - 1,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
				...new Array(len - 2).fill(''),
			]);
		}

		data.forEach((row, index) => {
			if (index === 0 || (data.length - 1 === index && lastHeaderCell)) {
				table.body.push(
					row.map((cellText, cellIndex) => {
						if (extraHeader !== '' && index === 0 && cellIndex === 0) {
							return {
								style: 'tableHeaderCell',
								text: '',
								alignment: alignment[cellIndex],
							};
						}

						return {
							style: 'tableHeaderCell',
							text: cellText,
							alignment: alignment[cellIndex],
						};
					}),
				);
			} else {
				const isLastRow = data.length - 1 === index && lastRowBold;
				const style = striped
					? index % 2 === 0
						? 'tableNormalCellStriped'
						: 'tableNormalCell'
					: 'tableNormalCell';

				table.body.push(
					row.map((cellText, cellIndex) => {
						// ROWSPAN — No usamos cell.rowSpan de pdfmake para evitar
						// conflicto con headerRows (cabeceras fantasma al paginar).
						// El texto se muestra solo en la primera fila del grupo;
						// las continuaciones quedan vacías.
						if (
							cellText &&
							typeof cellText === 'object' &&
							cellText.hasOwnProperty('value') &&
							cellText.hasOwnProperty('rowspan')
						) {
							if (cellText.value && cellText.rowspan >= 1) {
								return {
									style,
									text: cellText.value,
									alignment: alignment[cellIndex],
								};
							} else {
								return {
									style,
									text: '',
									alignment: alignment[cellIndex],
									border: [false, false, false, false],
								};
							}
						}
						// FIN ROWSPAN
						// Formatear números con formato europeo
						const formattedText =
							hideZeroNumeric && typeof cellText === 'number' && cellText === 0
								? ''
								: typeof cellText === 'number'
									? this.formatNumberEuropean(cellText)
									: cellText;
						return {
							style,
							text: formattedText,
							alignment: alignment[cellIndex],
							bold: isLastRow ? true : undefined,
						};
					}),
				);
			}
		});

		if (blueContainer) {
			blueContainer.stack[1].table.body[0].push({
				table,
				layout: this.customLayout,
				margin: [0, 2, 0, 0],
			});
			this.pdfData.content.push(blueContainer);
		} else {
			this.pdfData.content.push({
				table,
				layout: this.customLayout,
				margin: [0, 2, 0, 0],
			});
		}
	}

	insertTableTreatmentTable(title, data, extraData = [], type = 'default', groupConfig = null) {
		// Create main table
		const blueContainer = this.getBlueContainer(title, true);

		const alignment = Array(data[0].length).fill('left');

		// Set alignment for numeric columns
		if (data.length > 1) {
			for (let i = 1; i < data.length; i += 1) {
				data[i].forEach((cell, index) => {
					if (typeof cell === 'number') {
						alignment[index] = 'right';
					}
				});
			}
		}

		// Add header grouping row for UNITARIOS and TOTALES
		const numCols = data[0].length;
		let groupHeaderRow = null;
		const topMergedHeaders = [
			{
				text: data[0][0],
				rowSpan: 2,
				style: 'tableHeaderCell',
				alignment: alignment[0],
				margin: [1, 7, 1, 1],
			},
			{
				text: data[0][1],
				rowSpan: 2,
				style: 'tableHeaderCell',
				alignment: alignment[1],
				margin: [1, 7, 1, 1],
			},
		];

		if (groupConfig) {
			// Dynamic group header based on provided configuration
			const { dosisCols, totalesCols } = groupConfig;
			if (dosisCols > 0 || totalesCols > 0) {
				groupHeaderRow = [...topMergedHeaders];
				if (dosisCols > 0) {
					groupHeaderRow.push({
						text: 'Dosis unitaria',
						colSpan: dosisCols,
						style: 'tableHeaderCell',
						alignment: 'center',
					});
					for (let i = 1; i < dosisCols; i += 1) groupHeaderRow.push({});
				}
				if (totalesCols > 0) {
					groupHeaderRow.push({
						text: 'Totales',
						colSpan: totalesCols,
						style: 'tableHeaderCell',
						alignment: 'center',
					});
					for (let i = 1; i < totalesCols; i += 1) groupHeaderRow.push({});
				}
			}
		} else if (type === 'default' && numCols === 10) {
			// Default: Producto, Aplicación, [UNITARIOS: 5 cols], [TOTALES: 3 cols]
			groupHeaderRow = [
				...topMergedHeaders,
				{
					text: 'Dosis unitaria',
					colSpan: 5,
					style: 'tableHeaderCell',
					alignment: 'center',
				},
				{},
				{},
				{},
				{},
				{
					text: 'Totales',
					colSpan: 3,
					style: 'tableHeaderCell',
					alignment: 'center',
				},
				{},
				{},
			];
		} else if (type === 'planta' && numCols === 9) {
			// Planta: Producto, Aplicación, [UNITARIOS: 4 cols], [TOTALES: 3 cols]
			groupHeaderRow = [
				...topMergedHeaders,
				{
					text: 'Dosis unitaria',
					colSpan: 4,
					style: 'tableHeaderCell',
					alignment: 'center',
				},
				{},
				{},
				{},
				{
					text: 'Totales',
					colSpan: 3,
					style: 'tableHeaderCell',
					alignment: 'center',
				},
				{},
				{},
			];
		} else if (type === 'cuba' && numCols === 7) {
			// Cuba: Producto, Aplicación, [UNITARIOS: 3 cols], [TOTALES: 2 cols]
			groupHeaderRow = [
				...topMergedHeaders,
				{
					text: 'Dosis unitaria',
					colSpan: 3,
					style: 'tableHeaderCell',
					alignment: 'center',
				},
				{},
				{},
				{
					text: 'Totales',
					colSpan: 2,
					style: 'tableHeaderCell',
					alignment: 'center',
				},
				{},
			];
		}

		const table = {
			widths: Array(data[0].length).fill(
				`${(100 / data[0].length).toFixed(2)}%`,
			),
			headerRows: groupHeaderRow !== null ? 2 : 1,
			keepWithHeaderRows: 1,
			dontBreakRows: true,
			body: [],
		};

		if (groupHeaderRow !== null) {
			table.body.push(groupHeaderRow);
		}

		// Calculate totals only for the cost column
		const totals = Array(data[0].length).fill('');
		totals[0] = 'Total';

		if (data.length > 1) {
			// Support summing multiple columns: any header that contains 'precio' or 'cost'/'coste'
			// (but ignore headers that are clearly dose-related)
			const headers = data[0];
			const sumIndices = [];

			headers.forEach((header, idx) => {
				// Extract header text whether header is a string or an object/array
				let text = '';
				if (typeof header === 'string') {
					text = header;
				} else if (header && typeof header === 'object') {
					if (typeof header.text === 'string') {
						text = header.text;
					} else if (Array.isArray(header.text)) {
						text = header.text
							.map((t) => (typeof t === 'string' ? t : (t && t.text ? t.text : '')))
							.join(' ');
					}
				}

				text = (text || '').toLowerCase().trim();
				if (!text) return;

				// Avoid summing dose-related columns, but allow unit-based price headers
				// like 'Precio (€/kg-L)'. If 'kg' appears together with currency or the
				// word 'precio' treat it as a price column.
				const hasKg = /\bkg\b/.test(text);
				const hasCurrency = /€|eur|\bprecio\b/.test(text);
				if (/\bdosis\b/.test(text) || /g\/pl/.test(text) || (hasKg && !hasCurrency)) return;

				const isPrice = /\bprecio\b/.test(text) || hasCurrency;
				const isCost = /\b(cost|coste)\b/.test(text);

				if (isPrice || isCost) {
					sumIndices.push(idx);
				}
			});

			if (sumIndices.length > 0) {
				sumIndices.forEach((colIdx) => {
					let sum = 0;
					let hasNumericValues = false;

					for (let rowIndex = 1; rowIndex < data.length; rowIndex += 1) {
						const cell = data[rowIndex][colIdx];
						if (typeof cell === 'number' && !isNaN(cell) && isFinite(cell)) {
							sum += cell;
							hasNumericValues = true;
						}
					}

					if (hasNumericValues) {
						totals[colIdx] = Number(sum.toFixed(2));
					}
				});
			}
		}

		// Add rows
		data.forEach((row, index) => {
			if (index === 0) {
				// Header row
				table.body.push(
					row.map((cellText, cellIndex) => {
						if (groupHeaderRow !== null && cellIndex < 2) {
							return {
								style: 'tableHeaderCell',
								text: '',
								alignment: alignment[cellIndex],
							};
						}

						return {
							style: 'tableHeaderCell',
							text: cellText,
							alignment: alignment[cellIndex],
						};
					}),
				);
			} else {
				// Data rows
				table.body.push(
					row.map((cellText, cellIndex) => {
						const isNumericColumn = alignment[cellIndex] === 'right';
						let value = cellText;

						if (isNumericColumn && typeof value === 'number' && value === 0) {
							value = '';
						}

						const formattedValue =
							typeof value === 'number'
								? this.formatNumberEuropean(value)
								: value;

						return {
							style: 'tableNormalCell',
							text: formattedValue,
							alignment: alignment[cellIndex],
						};
					}),
				);
			}
		});

		// Add totals row
		table.body.push(
			totals.map((cellText, cellIndex) => {
				const isNumericColumn = alignment[cellIndex] === 'right';
				let value = cellText;

				if (isNumericColumn && typeof value === 'number' && value === 0) {
					value = '';
				}

				const formattedValue =
					typeof value === 'number'
						? this.formatNumberEuropean(value)
						: value;

				return {
					style: 'tableNormalCell',
					text: formattedValue,
					alignment: alignment[cellIndex],
					bold: true,
				};
			}),
		);

		// Create stack with extraData first, then table
		const stackContent = [];

		// Add extra data FIRST if provided (e.g., speed, plant density, etc.)
		if (extraData && extraData.length > 0) {
			const formattedExtraItems = extraData
				.filter((row) => Array.isArray(row) && row.length >= 2)
				.map((row) => ({
					title: row[0],
					value: row[1],
				}));

			const buildInfoCell = (item) => {
				if (!item) {
					return {
						style: 'noBordersCell',
						text: '',
					};
				}

				return {
					style: 'noBordersCell',
					text: [
						{
							text: `${item.title}: `,
							fontSize: 8,
							bold: false,
							color: '#545454',
						},
						{
							text: item.value,
							fontSize: 8,
							bold: false,
							color: '#000000',
						},
					],
				};
			};

			const extraBody = [];
			for (let i = 0; i < formattedExtraItems.length; i += 2) {
				extraBody.push([
					buildInfoCell(formattedExtraItems[i]),
					buildInfoCell(formattedExtraItems[i + 1]),
				]);
			}

			const extraTable = {
				widths: ['50%', '50%'],
				body: extraBody,
			};

			stackContent.push({
				table: extraTable,
				layout: this.noBordersLayout,
				margin: [0, 2, 0, 5],
			});
		}

		// Add main table
		stackContent.push({
			table,
			layout: this.customLayout,
			margin: [0, 2, 0, 0],
		});

		// Push the stack as a single element
		blueContainer.stack[1].table.body[0].push({
			stack: stackContent,
		});

		this.pdfData.content.push(blueContainer);
	}

	insertObservations(generalObservation, productsObservations, data) {
		// Siempre usa el blueContainer para mantener la estructura válida
		const blueContainer = this.getBlueContainer('Observaciones', false);

		const table = {
			widths: ['100%'],
			body: [],
		};

		// Observación general
		if (generalObservation) {
			table.body.push([
				{
					text: generalObservation,
					style: 'observationsTitle',
					margin: [0, 0, 0, 15],
				},
			]);
		}

		// Observaciones por producto
		if (
			Array.isArray(productsObservations) &&
			productsObservations.length > 0
		) {
			productsObservations.forEach((product) => {
				table.body.push([
					{
						ul: [
							{
								text: [
									{ text: `${product.name}: `, style: 'commentStyleTitle' },
									{ text: `${product.annotation}. `, style: 'normalStyle' },
								],
								style: 'observationsList',
							},
						],
					},
				]);
			});
		}

		// Observaciones detalladas
		if (Array.isArray(data)) {
			data.forEach((row) => {
				table.body.push([{ text: row.title, style: 'observationsTitle' }]);

				const comments = [];
				if (Array.isArray(row.comment)) {
					row.comment.forEach((comment) => {
						if (Array.isArray(comment) && comment.length >= 2) {
							comments.push({
								text: `${comment[0]}: `,
								style: 'commentStyleTitle',
							});
							comments.push({ text: `${comment[1]}. `, style: 'normalStyle' });
						}
					});
				}

				table.body.push([
					{
						style: 'observationsList',
						ul: [
							{ text: `Fecha: ${row.date}`, style: 'normalStyle' },
							{
								text: comments,
							},
						],
					},
				]);

				if (Array.isArray(row.multimedia)) {
					for (const multimediaItem of row.multimedia) {
						table.body.push([
							{
								image: multimediaItem.base64,
								width: 250,
								alignment: 'center',
								margin: [0, 0, 0, 15],
							},
						]);
					}
				}
			});
		}

		// Si no hay nada que mostrar, igual agrega la tabla vacía
		blueContainer.stack[1].table.body[0].push({
			table,
			layout: this.customLayout,
			margin: [0, 2, 0, 0],
		});
		this.pdfData.content.push(blueContainer);
	}

	insertTableBottomSignature(data) {
		const blueContainer = this.getBlueContainer(
			'Responsable de los cálculos',
			false,
		);

		const table = {
			widths: [],
			body: [],
		};

		// Validar que data existe y tiene elementos
		if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
			blueContainer.stack[1].table.body[0].push({ table, layout: this.noBordersLayout });
			this.pdfData.content.push(blueContainer);
			return;
		}

		const celldimension = (100 / data[0].length).toFixed(2);
		data[0].forEach((_) => {
			table.widths.push(`${celldimension}%`);
		});

		data.forEach((row) => {
			const headers = row.map((cell) => {
				// Validar que cell es un array con al menos 2 elementos
				if (!Array.isArray(cell) || cell.length < 2) {
					return {
						style: 'noBordersCellSignature',
						text: '',
					};
				}
				return {
					style: 'noBordersCellSignature',
					text: [
						{
							text: `${cell[0]}: `,
							fontSize: 8,
							bold: false,
							color: '#545454',
						},
						{ text: cell[1], fontSize: 8, bold: false, color: '#000000' },
					],
				};
			});
			table.body.push(headers);
		});

		table.body.push([
			{
				colSpan: 3,
				style: 'legalText',
				text: 'Este es un informe de recomendación. Timac Agro no se hará responsable en caso de un uso incorrecto del producto o de una indebida aplicación del mismo en campo por parte del cliente. Timac Agro no será responsable en la relación con la aplicación de ningún producto que no haya sido fabricado bajo su marca.',
			},
			'',
			'',
		]);

		blueContainer.stack[1].table.body[0].push({ table, layout: this.noBordersLayout });
		blueContainer.unbreakable = true;

		this.pdfData.content.push(blueContainer);
	}

	async insertTableAndGraph(title, tableData, domElement) {
		const blueContainer = this.getBlueContainer(title, false);
		const canvasToImage = await html2canvas(domElement);

		const table = {
			widths: Array(tableData[0].length).fill(
				`${(100 / tableData[0].length).toFixed(2)}%`,
			),
			headerRows: 2,
			dontBreakRows: true,
			body: [],
		};

		const graph = {
			widths: ['*'],
			body: [],
		};

		tableData.forEach((row, index) => {
			if (index === 0) {
				table.body.push([
					{ text: '', style: 'tableHeaderCell' },
					{
						text: 'Unitarios',
						colSpan: 5,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
					{ text: '', style: 'tableHeaderCell' },
					{ text: '', style: 'tableHeaderCell' },
					{ text: '', style: 'tableHeaderCell' },
					{
						text: 'Totales',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					'',
				]);

				table.body.push(
					row.map((cellText) => ({ style: 'tableHeaderCell', text: cellText })),
				);
			} else if (index === tableData.length - 1) {
				table.body.push(
					row.map((cellText) => ({ style: 'tableHeaderCell', text: cellText })),
				);
			} else {
				table.body.push(
					row.map((cellText) => ({
						style: 'tableNormalCell',
						text: cellText,
						Wrap: true,
					})),
				);
			}
		});

		graph.body.push([
			{
				image: canvasToImage.toDataURL(),
				width: 520,
			},
		]);

		blueContainer.stack[1].table.body[0].push({
			columns: [
				{ table, layout: this.customLayout, margin: [0, 2, 10, 0] },
				{
				table: graph,
				layout: this.customLayout,
				alignment: 'center',
				margin: [0, 2, 0, 0],
				},
			],
		});

		this.pdfData.content.push(blueContainer);
	}

	assignColumns(showPlant, tableComplete, tableNoCost) {
		switch (true) {
			case showPlant && tableComplete:
				return 12;
			case showPlant && tableNoCost:
				return 7;
			case !showPlant && tableComplete:
				return 9;
			case !showPlant && tableNoCost:
				return 5;
			default:
				return undefined; // Si ninguna condición se cumple
		}
	}

	insertResumePerProduct(
		title,
		tableData,
		showPlant,
		tableComplete,
		tableNoCost,
		nutritionIrrigationData,
	) {
		if (!tableComplete && !tableNoCost) return;

		tableData = Array.isArray(tableData) ? tableData.filter(Array.isArray) : [];
		const blueContainer = this.getBlueContainer(title, false);
		const isComplete = showPlant && tableComplete;

		let columns = this.assignColumns(showPlant, tableComplete, tableNoCost);

		// Configuración del ancho de las columnas si se cumple isComplete el ancho de Productos sera más ancho
		const widths = isComplete
			? ['*', ...Array(columns - 1).fill('auto')]
			: Array(columns).fill(`${(100 / columns).toFixed(2)}%`);

		const table = {
			widths,
			headerRows: 2,
			dontBreakRows: true,
			body: [],
		};
		// Generar cabecera y unidades según lógica original
		let headerRow, unitsRow;
		if (showPlant && tableComplete) {
			headerRow = [
				{
					text: 'Producto',
					rowSpan: 2,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
			];
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				headerRow.push(
					{
						text: 'Dosis unitaria',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				headerRow.push(
					{
						text: 'Dosis por planta',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			if (
				nutritionIrrigationData &&
				nutritionIrrigationData.showTotals === true
			)
				headerRow.push(
					{
						text: 'Dosis total',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			headerRow.push(
				{
					text: 'Precio',
					colSpan: 2,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
				{ text: '', style: 'tableHeaderCell' },
				{
					text: i18n.t('nutritionDetail.productResume.costUnit'),
					alignment: 'left',
					rowSpan: 2,
					style: 'tableHeaderCell',
					Wrap: true,
				},
				{
					text: i18n.t('nutritionDetail.productResume.costPlant'),
					alignment: 'left',
					rowSpan: 2,
					style: 'tableHeaderCell',
					Wrap: true,
				},
				{
					text: i18n.t('nutritionDetail.productResume.costTotal'),
					alignment: 'left',
					rowSpan: 2,
					style: 'tableHeaderCell',
					Wrap: true,
				},
			);
			unitsRow = [{ text: '', style: 'tableHeaderCell' }];
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				unitsRow.push(
					{ text: 'kg/ha', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'l/ha', style: 'tableHeaderCell', alignment: 'right' },
				);
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				unitsRow.push(
					{ text: 'g/planta', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'ml/planta', style: 'tableHeaderCell', alignment: 'right' },
				);
			if (
				nutritionIrrigationData &&
				nutritionIrrigationData.showTotals === true
			)
				unitsRow.push(
					{ text: 'kg', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'l', style: 'tableHeaderCell', alignment: 'right' },
				);
			unitsRow.push(
				{ text: '€/kg', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '€/l', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '', style: 'tableHeaderCell', alignment: 'right' },
			);
		} else if (showPlant && tableNoCost) {
			headerRow = [
				{
					text: 'Producto',
					rowSpan: 2,
					alignment: 'left',
					style: 'tableHeaderCell',
					Wrap: true,
				},
			];
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				headerRow.push(
					{
						text: 'Dosis unitaria',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				headerRow.push(
					{
						text: 'Dosis por planta',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			if (
				nutritionIrrigationData &&
				nutritionIrrigationData.showTotals === true
			)
				headerRow.push(
					{
						text: 'Dosis total',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			unitsRow = [{ text: '', style: 'tableHeaderCell' }];
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				unitsRow.push(
					{ text: 'kg/ha', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'l/ha', style: 'tableHeaderCell', alignment: 'right' },
				);
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				unitsRow.push(
					{ text: 'g/planta', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'ml/planta', style: 'tableHeaderCell', alignment: 'right' },
				);
			if (
				nutritionIrrigationData &&
				nutritionIrrigationData.showTotals === true
			)
				unitsRow.push(
					{ text: 'kg', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'l', style: 'tableHeaderCell', alignment: 'right' },
				);
		} else if (!showPlant && tableComplete) {
			headerRow = [
				{
					text: 'Producto',
					rowSpan: 2,
					alignment: 'left',
					style: 'tableHeaderCell',
					Wrap: true,
				},
			];
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				headerRow.push(
					{
						text: 'Dosis unitaria',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				headerRow.push(
					{
						text: 'Dosis por planta',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			if (
				nutritionIrrigationData &&
				nutritionIrrigationData.showTotals === true
			)
				headerRow.push(
					{
						text: 'Dosis total',
						colSpan: 2,
						alignment: 'center',
						style: 'tableHeaderCell',
					},
					{ text: '', style: 'tableHeaderCell' },
				);
			headerRow.push(
				{
					text: 'Precio',
					colSpan: 2,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
				{ text: '', style: 'tableHeaderCell' },
				{
					text: i18n.t('nutritionDetail.productResume.costUnit'),
					alignment: 'right',
					rowSpan: 2,
					style: 'tableHeaderCell',
					Wrap: true,
				},
				{
					text: i18n.t('nutritionDetail.productResume.costTotal'),
					alignment: 'right',
					rowSpan: 2,
					style: 'tableHeaderCell',
					Wrap: true,
				},
			);
			unitsRow = [{ text: '', style: 'tableHeaderCell' }];
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				unitsRow.push(
					{ text: 'kg/ha', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'l/ha', style: 'tableHeaderCell', alignment: 'right' },
				);
			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				unitsRow.push(
					{ text: 'g/planta', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'ml/planta', style: 'tableHeaderCell', alignment: 'right' },
				);
			if (
				nutritionIrrigationData &&
				nutritionIrrigationData.showTotals === true
			)
				unitsRow.push(
					{ text: 'kg', style: 'tableHeaderCell', alignment: 'right' },
					{ text: 'l', style: 'tableHeaderCell', alignment: 'right' },
				);
			unitsRow.push(
				{ text: '€/kg', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '€/l', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '', style: 'tableHeaderCell', alignment: 'right' },
				{ text: '', style: 'tableHeaderCell', alignment: 'right' },
			);
		} else {
			headerRow = [
				{
				text: 'Producto',
				rowSpan: 2,
				alignment: 'left',
				style: 'tableHeaderCell',
				Wrap: true,
				fontSize: 8,
				},
			];

			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				headerRow.push(
				{
					text: 'Dosis unitaria',
					colSpan: 2,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
				{ text: '', style: 'tableHeaderCell' },
			);

			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				headerRow.push(
				{
					text: 'Dosis por planta',
					colSpan: 2,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
				{ text: '', style: 'tableHeaderCell' },
			);

			if (nutritionIrrigationData && nutritionIrrigationData.showTotals === true)
				headerRow.push(
				{
					text: 'Dosis total',
					colSpan: 2,
					alignment: 'center',
					style: 'tableHeaderCell',
				},
				{ text: '', style: 'tableHeaderCell' },
			);

			unitsRow = [{ text: '', style: 'tableHeaderCell' }];

			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 1)
				unitsRow.push(
				{ text: 'kg/ha', style: 'tableHeaderCell', alignment: 'right' },
				{ text: 'l/ha', style: 'tableHeaderCell', alignment: 'right' },
			);

			if (nutritionIrrigationData && nutritionIrrigationData.dosis_unit == 2)
				unitsRow.push(
				{ text: 'g/planta', style: 'tableHeaderCell', alignment: 'right' },
				{ text: 'ml/planta', style: 'tableHeaderCell', alignment: 'right' },
			);

			if (nutritionIrrigationData && nutritionIrrigationData.showTotals === true)
				unitsRow.push(
				{ text: 'kg', style: 'tableHeaderCell', alignment: 'right' },
				{ text: 'l', style: 'tableHeaderCell', alignment: 'right' },
			);
		}


		// --- Calcular el máximo de columnas ---
		let maxCols = Math.max(
			headerRow.length,
			unitsRow.length,
			...tableData.map((r) => r.length),
			1,
		);

		// widths dinámico
		table.widths = Array(maxCols).fill(`${(100 / maxCols).toFixed(2)}%`);

		// --- Añadir filas ---
		function fitRow(row, targetLength) {
			const r = [...row];
			while (r.length < targetLength) r.push({ text: '', style: 'tableHeaderCell' });
			return r;
		}

		table.body.push(fitRow(headerRow, maxCols));
		table.body.push(fitRow(unitsRow, maxCols));

		// Determine cost column start index for totals row validation
		let costColStart = -1;
		if (tableComplete) {
			const numCostCols = showPlant ? 3 : 2;
			costColStart = maxCols - numCostCols;
		}

		for (let i = 0; i < tableData.length; i++) {
			const row = tableData[i];
			let isLast = i === tableData.length - 1;
			let dataRow = row.map((cellText, cellIndex) => {
				let displayText;
				if (cellIndex === 0) {
					displayText = cellText ?? '';
				} else if (isLast && (costColStart < 0 || cellIndex < costColStart)) {
					// In totals row, only show cost columns
					displayText = '';
				} else {
					displayText = this.formatNumberEuropean(cellText);
				}

				return {
					style: 'tableNormalCell',
					text: displayText,
					alignment: cellIndex === 0 ? 'left' : 'right',
					fontSize: 8,
					Wrap: true,
					bold: isLast ? true : undefined,
				};
			});
			dataRow = fitRow(dataRow, maxCols);
			table.body.push(dataRow);
		}
		// Agregar la tabla al contenedor y al PDF
		blueContainer.stack[1].table.body[0].push({
			table,
			layout: this.customLayout,
			margin: [0, 2, 0, 0],
		});
		this.pdfData.content.push(blueContainer);
	}

	async insertGenericChart(title, domElement) {
		const canvasToImage = await html2canvas(domElement);

		const graph = {
			widths: ['*'],
			body: [],
		};

		graph.body.push([
			{
				image: canvasToImage.toDataURL(),
				width: 520,
			},
		]);

		// Si no hay título, insertar directamente sin blueContainer
		if (!title || title.trim() === '') {
			this.pdfData.content.push({
				table: graph,
				layout: this.customLayout,
				alignment: 'center',
				margin: [0, 2, 0, 0],
			});
		} else {
			const blueContainer = this.getBlueContainer(title, true);
			blueContainer.unbreakable = true;
			blueContainer.stack[1].table.body[0].push({
				table: graph,
				layout: this.customLayout,
				alignment: 'center',
				margin: [0, 2, 0, 0],
			});
			this.pdfData.content.push(blueContainer);
		}
	}

	insertReportTitle(text) {
		this.pdfData.content.push({ text, style: 'reportTitleStyle' });
	}

	insertReportSubtitle(text) {
		if (text === null || text === undefined || String(text).trim() === '') return;
		this.pdfData.content.push({ text: String(text), style: 'reportSubtitleStyle' });
	}

	async getPdfAsBuffer() {
		const pdfDocGenerator = pdfmake.createPdf(cloneDeep(this.pdfData));
		return new Promise((resolve) => {
			pdfDocGenerator.getBuffer((buffer) => {
				resolve(buffer);
			});
		});
	}

	async buildAndDownload(filename) {
		return new Promise((resolve) => {
			pdfmake.createPdf(cloneDeep(this.pdfData)).download(filename, () => {
				resolve();
			});
		});
	}

	buildForMobile() {
		return pdfmake.createPdf(cloneDeep(this.pdfData));
	}
}
